import { format, isSameDay, parseISO } from "date-fns";
import { useTranslations } from "next-intl";
import React, { useCallback, useMemo } from "react";

import { DateRange, DateRangePicker } from "@zapier/design-system";

import { useHasHydrated } from "@zapier/reporting/src/hooks";
import { DateFilterType } from "@zapier/reporting/src/types";

type DesignSystemProps = React.ComponentPropsWithoutRef<typeof DateRangePicker>;
type IncompatibleProps = "onChange" | "value";
type OptionalProps = "aria-label" | "menuAriaLabel";
type UpstreamProps = Omit<
  DesignSystemProps,
  IncompatibleProps | OptionalProps
> &
  Partial<Pick<DesignSystemProps, OptionalProps>>;

export interface DisabledDates {
  before: Date;
  after?: Date;
}

interface DateFilterProps extends UpstreamProps {
  /** Pass to render selected items outside of dropdown */
  children?: (props: {
    from: string;
    hasValue: boolean;
    to: string;
  }) => React.ReactElement;
  /** Key of the default option (to determine if filter should have a close button) */
  defaultOptionKey?: string;
  id?: string;
  onChange: (item: Partial<DateFilterType>) => void;
  onDayClick?: (day: Date) => void;
  value: DateFilterType;
  placeholder?: string;
  disabledDates?: DisabledDates;
}

function isSameDayOrUnset(
  a: Date | null | undefined,
  b: Date | null | undefined
) {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return isSameDay(a, b);
}

export const DateFilter = (props: DateFilterProps) => {
  const t = useTranslations("date-filter");
  const hasHydrated = useHasHydrated();
  const { children, onChange, ...dateRangePickerProps } = props;

  const defaultOption = props.defaultOptionKey
    ? props.options?.find((opt) => opt.key === props.defaultOptionKey)
    : props.options?.[0];

  const handleChange = useCallback(
    (value: DateRange) => {
      const { from, to } = value;

      const dateFilter = {
        hasChange: true,
        ...(from ? { from: format(from, "yyyy-MM-dd") } : {}),
        ...(to ? { to: format(to, "yyyy-MM-dd") } : {}),
      };

      onChange(dateFilter);
    },
    [onChange]
  );

  const hasValue = defaultOption
    ? !isSameDayOrUnset(
        defaultOption.value.from,
        props.value.from ? parseISO(props.value.from) : undefined
      ) ||
      !isSameDayOrUnset(
        defaultOption.value.to,
        props.value.to ? parseISO(props.value.to) : undefined
      )
    : false;

  const selectionValue = useMemo(() => {
    const from = props.value.from && new Date(props.value.from);
    const to = props.value.to && new Date(props.value.to);

    return {
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    };
  }, [props.value.from, props.value.to]);

  if (!hasHydrated) {
    return null;
  }

  return (
    <>
      <DateRangePicker
        {...dateRangePickerProps}
        aria-label={t("aria-label")}
        closeText={t("close-text")}
        id={props.id}
        menuAlign={props.menuAlign ? props.menuAlign : "left"}
        menuAriaLabel={t("menu-aria-label")}
        onChange={handleChange}
        options={props.options}
        placeholder={
          props.placeholder ? `${props.placeholder}` : t("placeholder")
        }
        size={props.size}
        value={selectionValue}
        disabledDates={props.disabledDates}
        onDayClick={props.onDayClick}
      />
      {children?.({
        from: props.value.from,
        hasValue,
        to: props.value.to,
      })}
    </>
  );
};
